import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    Box, Button, Card, CardHeader, CardContent, Collapse, Typography,
    Grid, FormControl, FormGroup, FormLabel, OutlinedInput,
    Select, MenuItem, Chip
} from '@material-ui/core'
import Loader from 'components/general/Loader'
import AdminAPI from 'config/api/AdminAPI'
import ProviderAPI from 'config/api/ProviderAPI'
import { onError } from 'config/lib/errorLib'
import { NotificationManager } from 'react-notifications'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SendEmailModal from 'components/admin/SendEmailModal'
import SendCardLinkModal from 'components/admin/SendCardLinkModal'
import { productTypes } from 'config/constants/Constants'
import { formatDomain } from 'config/helpers/Helpers'

const useStyles = makeStyles(componentStyles)

function AdminDetailsCard({ companyDetails, updateField, updateCompany }) {
    const classes = { ...useStyles() }
    const [open, setOpen] = useState(true)
    const api = new AdminAPI()
    const providerApi = new ProviderAPI()

    const name = companyDetails.name || ''
    const url = companyDetails.url || ''
    const slug = companyDetails.slug || ''
    const profileStatus = companyDetails.profileStatus || 'Not Published'
    const profileVerified = companyDetails.profileVerified || 'Not Verified'
    const badgeLink = companyDetails.badgeLink || 'None'
    const profileSubmitted = companyDetails.profileSubmitted
    const verificationSubmitted = companyDetails.verificationSubmitted
    const billingEnabled = companyDetails.billingEnabled || 'No'
    const metaDescription = companyDetails.metaDescription || ''
    // Plan type now comes directly from companyDetails (admin can override independently of coverageType)
    const planType = companyDetails.planType || 'topup'

    // Products available
    const productsAvailable = companyDetails.productsAvailable || {}
    const selectedProducts = productTypes.filter(pt => productsAvailable[pt])

    // Badge status
    const [checkingStatus, setCheckingStatus] = useState(false)
    const [badgeVerified, setBadgeVerified] = useState(companyDetails.badgeVerified || false)

    // Registration cert
    const registrationCert = companyDetails.verification ? companyDetails.verification.registrationCert : null

    const [loading, setLoading] = useState(false)
    const [sendEmailOpen, setSendEmailOpen] = useState(false)
    const [sendCardLinkOpen, setSendCardLinkOpen] = useState(false)

    async function saveDetails() {
        setLoading(true)
        try {
            const updatedCompany = await api.updateProfileSettings(
                companyDetails.companyId,
                name,
                formatDomain(url),
                slug,
                profileStatus,
                profileVerified,
                badgeLink,
                billingEnabled,
                metaDescription,
                planType,
                productsAvailable
            )
            console.log('updatedCompany: ', updatedCompany) 
            if (updatedCompany && updatedCompany.success === false) {
                NotificationManager.error(updatedCompany.message)

            }
            else {
                updateCompany(updatedCompany)
                NotificationManager.success('Settings saved')
            }
        } catch (e) {
            onError(e)
        }
        setLoading(false)
    }

    function previewProfile() {
        if (!companyDetails.slug) {
            NotificationManager.error('Please add a slug first')
        }
        else {
            const previewUrl = `https://installers.energyefficiency.ie/preview-profile?slug=${companyDetails.slug}`;
            window.open(previewUrl, '_blank', 'noopener,noreferrer');
        }
    }

    function checkRequiredFields() {
        return !name.trim() || !url.trim() || !slug.trim()
    }

    // --- Badge status logic ---
    const handleBadgeCheck = async () => {
        setCheckingStatus(true)
        try {
            const response = await providerApi.checkWebsiteBadge(companyDetails.companyId, url)
            setBadgeVerified(response.verified)
            NotificationManager.success('Status refreshed')
        } catch (e) {
            NotificationManager.error('Could not check badge status')
        }
        setCheckingStatus(false)
    }

    // --- Download/view registration cert ---
    const handleDownload = () => {
        if (typeof registrationCert === 'string') {
            window.open(registrationCert, '_blank', 'noopener,noreferrer')
        }
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <Box display='flex' style={{ justifyContent: 'space-between' }}>
                        <Box>
                            <Box component={Typography} variant='h2' marginBottom='0!important'>
                                <Box component='span'>Settings - {companyDetails.name}</Box>
                            </Box>
                            <Box
                                component='p'
                                fontSize='.875rem'
                                marginBottom='0'
                                marginTop='0'
                                lineHeight='1.7'
                                fontWeight='300'
                            >
                                Internal admin controls and metadata
                            </Box>
                        </Box>
                    </Box>
                }
                onClick={() => setOpen(o => !o)}
                style={{ cursor: 'pointer' }}
                action={<ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginTop: 4 }} />}
                classes={{ root: classes.cardHeaderRoot }}
            />
            <Collapse in={open}>
            <CardContent>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Company Name</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <OutlinedInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='My Company'
                                    value={name}
                                    onChange={(e) => updateField('name', e.target.value)}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Website URL</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <OutlinedInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='www.mycompany.com'
                                    value={url}
                                    onChange={(e) => updateField('url', e.target.value)}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Slug</FormLabel>
                            <FormControl variant='filled' component={Box} width='100%'>
                                <OutlinedInput
                                    value={slug}
                                    onChange={(e) => updateField('slug', e.target.value)}
                                    placeholder='e.g. green-solar-solutions'
                                    autoComplete='off'
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Profile Status</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={profileStatus}
                                    onChange={(e) => updateField('profileStatus', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    <MenuItem value='Not Published'>Not Published</MenuItem>
                                    <MenuItem value='Published'>Published</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Profile Verified</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={profileVerified}
                                    onChange={(e) => updateField('profileVerified', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    <MenuItem value='Not Verified'>Not Verified</MenuItem>
                                    <MenuItem value='Verified'>Verified</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Badge Link Location</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={badgeLink}
                                    onChange={(e) => updateField('badgeLink', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{ marginBottom: '0.5rem' }}
                                >
                                    <MenuItem value='None'>None</MenuItem>
                                    <MenuItem value='Profile'>Profile</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Billing Enabled</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={billingEnabled}
                                    onChange={(e) => updateField('billingEnabled', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{ marginBottom: '0.5rem' }}
                                >
                                    <MenuItem value='Yes'>Yes</MenuItem>
                                    <MenuItem value='No'>No</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Plan Type</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={planType}
                                    onChange={(e) => updateField('planType', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{ marginBottom: '0.5rem' }}
                                >
                                    <MenuItem value='topup'>Topup</MenuItem>
                                    <MenuItem value='weekly'>Weekly (Arrears)</MenuItem>
                                    <MenuItem value='monthly'>Monthly (Arrears)</MenuItem>
                                </Select>
                            </FormControl>
                            <Typography variant='caption' style={{ marginTop: 4, display: 'block', color: '#8898aa' }}>
                                Adjust the billing model independently of coverage selection.
                            </Typography>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>Products Available</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple
                                    value={selectedProducts}
                                    onChange={(e) => {
                                        const selected = e.target.value
                                        const updated = {}
                                        productTypes.forEach(pt => {
                                            updated[pt] = selected.includes(pt)
                                        })
                                        updateField('productsAvailable', updated)
                                    }}
                                    input={<OutlinedInput />}
                                    renderValue={(selected) => (
                                        <Box display='flex' flexWrap='wrap' style={{ gap: 4 }}>
                                            {selected.map(value => (
                                                <Chip key={value} label={value} size='small' />
                                            ))}
                                        </Box>
                                    )}
                                    style={{ marginBottom: '0.5rem' }}
                                >
                                    {productTypes.map(pt => (
                                        <MenuItem key={pt} value={pt}>{pt}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant='caption' style={{ marginTop: 4, display: 'block', color: '#8898aa' }}>
                                Select which products this provider offers.
                            </Typography>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>SEO Meta Description</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <OutlinedInput
                                    autoComplete='off'
                                    multiline
                                    rows={4}
                                    placeholder='Short summary for search engines (recommended 120–160 characters)'
                                    value={metaDescription}
                                    onChange={(e) => updateField('metaDescription', e.target.value)}
                                />
                            </FormControl>
                            <Typography variant='caption' style={{ marginTop: 4, display: 'block', color: '#8898aa' }}>
                                This is used for the meta description tag on the provider profile page.
                            </Typography>
                        </FormGroup>
                    </Grid>

                    {/* Badge Status Section */}
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>Website Badge Status</FormLabel>
                            <Box display='flex' alignItems='center' mt={1}>
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    onClick={handleBadgeCheck}
                                    disabled={checkingStatus}
                                    style={{ marginRight: 16 }}
                                >
                                    {checkingStatus ? 'Checking...' : 'Refresh Badge Status'}
                                </Button>
                                <Typography
                                    variant='body2'
                                    style={{
                                        color: badgeVerified ? '#229626' : '#cc0000',
                                        fontWeight: 500
                                    }}
                                >
                                    {badgeVerified
                                        ? '✅ Badge is live on homepage'
                                        : '❌ Badge not detected on homepage'}
                                </Typography>
                            </Box>
                        </FormGroup>
                    </Grid>

                    {/* Registration Cert Section */}
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Proof of Business Registration</FormLabel>
                            {registrationCert ? (
                                <Box display="flex" alignItems="center">
                                    <Typography variant='body2'>
                                        Proof of Registration Uploaded
                                    </Typography>
                                    {typeof registrationCert === 'string' && (
                                        <Button
                                            color="primary"
                                            style={{ marginLeft: 8, textTransform: 'none', fontSize: '1rem', padding: 0, minWidth: 0 }}
                                            onClick={handleDownload}
                                        >
                                            (View/Download)
                                        </Button>
                                    )}
                                </Box>
                            ) : (
                                <Typography variant='body2'>No document uploaded</Typography>
                            )}
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>Profile Completion</FormLabel>
                            <Box mt={1}>
                                <Typography variant='body2'>
                                    <strong>Company Details Submitted:</strong> {profileSubmitted ? 'Yes' : 'No'}
                                </Typography>
                                <Typography variant='body2'>
                                    <strong>Verification Submitted:</strong> {verificationSubmitted ? 'Yes' : 'No'}
                                </Typography>
                            </Box>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='space-between' width='100%'>
                            <Box>
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    onClick={() => previewProfile()}
                                    style={{ marginRight: '1rem' }}
                                >
                                    Preview Profile
                                </Button>
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    onClick={() => setSendEmailOpen(true)}
                                    style={{ marginRight: '1rem' }}
                                >
                                    Send Email
                                </Button>
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    onClick={() => setSendCardLinkOpen(true)}
                                    style={{ marginRight: '1rem' }}
                                >
                                    Send Add-Card Link
                                </Button>
                            </Box>
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={loading || checkRequiredFields()}
                                onClick={() => saveDetails()}
                            >
                                {!loading ? (
                                    <>Save</>
                                ) : (
                                    <Loader height={12} />
                                )}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
            </Collapse>

            {/* Send Email Modal */}
            <SendEmailModal
                open={sendEmailOpen}
                onClose={() => setSendEmailOpen(false)}
                companyDetails={companyDetails}
            />

            {/* Send Add-Card Link Modal */}
            <SendCardLinkModal
                open={sendCardLinkOpen}
                onClose={() => setSendCardLinkOpen(false)}
                companyDetails={companyDetails}
            />
        </Card>
    )
}

export default AdminDetailsCard